/**
 * Check if the code is running in a browser environment.
 */
export function isBrowser() {
  return typeof window !== 'undefined';
}

/**
 * @name prefixReference
 * @description Name a record by its reference, adding the noun only when the
 * reference does not already start with it.
 *
 * References here are frequently human-readable rather than coded: an order
 * with no supplier reference is auto-numbered "Order 2", so the obvious
 * `Order ${reference}` template produced "Order Order 2 was created".
 * `kit.prefix_reference` in the database applies the same rule to notification
 * text raised by triggers.
 */
export function prefixReference(
  prefix: string,
  reference: string | null | undefined,
): string {
  const value = reference?.trim() ?? '';

  if (!value) {
    return prefix;
  }

  const escapedPrefix = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  return new RegExp(`^${escapedPrefix}([^A-Za-z]|$)`, 'i').test(value)
    ? value
    : `${prefix} ${value}`;
}

/**
 * Production-order statuses are stored as integers (1–8). The notification
 * trigger stringifies the raw column, so the popover shows "Order 1 is now 2".
 * Only the numeric codes are unambiguous — slug labels collide across orders,
 * POs, and shipments.
 *
 * These must be ORDER_LIFECYCLE's labels, the app's storable vocabulary in
 * apps/web/lib/purchase-orders/order-lifecycle.ts, and the database's
 * `kit.production_order_status_label` must say the same. This table had
 * drifted: it called 6 "Shipped" and 7 "In Transit" where the app says
 * "Ready to Ship" and "Shipped", so marking an order Ready announced that it
 * had shipped. `order-status-labels.test.ts` now holds the two together.
 */
export const PRODUCTION_ORDER_STATUS_LABELS: Record<string, string> = {
  '1': 'Quoted',
  '2': 'Deposit Paid',
  '3': 'Confirmed',
  '4': 'In Production',
  '5': 'Quality Check',
  '6': 'Ready to Ship',
  '7': 'Shipped',
  '8': 'Delivered',
};

/**
 * How a status change reads as a sentence about the record: "Order 1 is now
 * Shipped" is not English, so each stage gets its own predicate. `{subject}`
 * is the record's name ("Order 1"). Pipeline stages read "is now in ...";
 * one-off events (quoted, shipped, delivered) read as things that happened.
 */
const PRODUCTION_ORDER_STATUS_PHRASES: Record<string, string> = {
  '1': '{subject} has been quoted',
  '2': "{subject}'s deposit has been paid",
  '3': '{subject} has been confirmed',
  '4': '{subject} is now In Production',
  '5': '{subject} is now in Quality Check',
  '6': '{subject} is Ready to Ship',
  '7': '{subject} has shipped',
  '8': '{subject} has been delivered',
};

/**
 * @name formatNotificationStatusText
 * @description Rewrite stored notification copy that still contains a raw
 * numeric status so the popover reads naturally: "Order 1 is now 4" becomes
 * "Order 1 is now in Production", and "moved from 4 to 5" becomes
 * "moved from Production to Quality Check".
 */
export function formatNotificationStatusText(text: string): string {
  const withLabels = text.replace(
    /\b((?:moved|changed) from)\s+(\d+)\s+to\s+(\d+)\b/gi,
    (match, connector: string, fromToken: string, toToken: string) => {
      const fromLabel = PRODUCTION_ORDER_STATUS_LABELS[fromToken];
      const toLabel = PRODUCTION_ORDER_STATUS_LABELS[toToken];

      return fromLabel && toLabel
        ? `${connector} ${fromLabel} to ${toLabel}`
        : match;
    },
  );

  return withLabels.replace(
    /(\S(?:.*?\S)?)\s+is now\s+(\d+)(\.?)$/i,
    (match, subject: string, token: string, period: string) => {
      const phrase = PRODUCTION_ORDER_STATUS_PHRASES[token];

      return phrase
        ? phrase.replace('{subject}', () => subject) + period
        : match;
    },
  );
}

/**
 * Events that email somebody about their own account's activity.
 *
 * These are the ones the global switch governs, and they are exactly the set
 * that was hardcoded off before. Founder alerts sit outside it —
 * `new_user_signup`, `landed_cost_mismatch`,
 * `unlinked_shipping_document`, `smart_upload_failed` and
 * `unclassified_document` — and have always sent.
 *
 * `supplier_invited` is listed. It is the team's heads-up that somebody invited
 * a supplier, not the invitation itself - that one is sent directly by
 * `supplier-invitations.service.ts` and never passed through here, so gating
 * this event cannot strand an invitation.
 */
const USER_FACING_NOTIFICATION_EMAIL_EVENT_TYPES = new Set([
  'supplier_message',
  'supplier_document_uploaded',
  'supplier_order_proposal',
  'supplier_profile_request',
  'supplier_document_removed',
  'supplier_payment_confirmed',
  'supplier_shipment_contents',
  'supplier_invited',
  'purchase_order_created',
  'purchase_order_updated',
  'production_order_created',
  'production_order_status_changed',
  'shipment_created',
  'shipment_status_changed',
]);

/**
 * Events whose emails stay inside the organization.
 *
 * Suppliers keep their in-app notification for all of these - the recipient
 * resolution behind that is unchanged - but they are not emailed about the
 * buyer's internal order and shipment churn. A supplier contact is an external
 * party who never asked for it, and a status field moving from 4 to 5 is the
 * buying team's business.
 */
const INTERNAL_ONLY_NOTIFICATION_EMAIL_EVENT_TYPES = new Set([
  // Proposal decisions reach supplier contacts in-app through their portal.
  // The shared email template still links to internal notification settings.
  'supplier_order_proposal',
  'supplier_shipment_contents',
  'supplier_document_uploaded',
  'supplier_payment_confirmed',
  'purchase_order_created',
  'purchase_order_updated',
  'production_order_created',
  'production_order_status_changed',
  'shipment_created',
  'shipment_status_changed',
]);

/**
 * @name isNotificationEmailEnabled
 * @description Whether an event type may be emailed at all.
 *
 * User-facing notification emails are off unless `NOTIFICATION_EMAILS_ENABLED`
 * is set to `true`. Supplier conversation mail can be enabled separately with
 * `SUPPLIER_MESSAGE_EMAILS_ENABLED`, without opening every other event.
 *
 * Fails closed. Anything other than the exact string `true` - unset, empty,
 * `1`, `TRUE ` with a stray space - leaves them off, because the failure mode
 * of guessing wrong here is mailing every customer.
 */
export function isNotificationEmailEnabled(eventType: string) {
  if (!USER_FACING_NOTIFICATION_EMAIL_EVENT_TYPES.has(eventType)) {
    return true;
  }

  if (
    eventType === 'supplier_message' &&
    process.env.SUPPLIER_MESSAGE_EMAILS_ENABLED === 'true'
  ) {
    return true;
  }

  return process.env.NOTIFICATION_EMAILS_ENABLED === 'true';
}

/**
 * @name isNotificationEmailRecipientAllowed
 * @description Whether this particular recipient may be emailed about the
 * event, on top of the event itself being enabled. Suppliers are excluded from
 * internal order and shipment traffic.
 */
export function isNotificationEmailRecipientAllowed(
  eventType: string,
  isSupplier: boolean,
) {
  if (!isSupplier) {
    return true;
  }

  return !INTERNAL_ONLY_NOTIFICATION_EMAIL_EVENT_TYPES.has(eventType);
}

/**
 * @name formatCurrency
 * @description Format the currency based on the currency code
 */
export function formatCurrency(params: {
  currencyCode: string;
  locale: string;
  value: string | number;
}) {
  const [lang, region] = params.locale.split('-');

  return new Intl.NumberFormat(region ?? lang, {
    style: 'currency',
    currency: params.currencyCode,
  }).format(Number(params.value));
}

/**
 * @name isSafeRedirectPath
 * @description Checks if a path is safe for redirects (prevents open redirect attacks).
 * Safe paths must:
 * - Start with a single `/`
 * - NOT start with `//` (protocol-relative URLs)
 * - NOT contain `://` (absolute URLs)
 * - NOT contain backslash (URL normalization attacks)
 */
export function isSafeRedirectPath(path: string): boolean {
  if (!path || typeof path !== 'string') return false;

  // Must start with exactly one forward slash (relative path)
  if (!path.startsWith('/') || path.startsWith('//')) return false;

  // Must not contain protocol indicators
  if (path.includes('://')) return false;

  // Must not contain backslashes (can be normalized to forward slashes)
  if (path.includes('\\')) return false;

  return true;
}

/**
 * @name getSafeRedirectPath
 * @description Returns the path if safe, otherwise returns the fallback.
 * Use this to validate user-supplied redirect URLs to prevent open redirect attacks.
 */
export function getSafeRedirectPath(
  path: string | null | undefined,
  fallback: string,
): string {
  if (path && isSafeRedirectPath(path)) {
    return path;
  }

  return fallback;
}
